import React, { useState } from 'react';
import {
  ActionList,
  ActionListItem,
  Amount,
  Box,
  Button,
  Dropdown,
  DropdownOverlay,
  Heading,
  SearchInput,
  SelectInput,
  Spinner,
  Text,
} from '@razorpay/blade/components';
import { PolicySummary } from './PolicySummary';
import { useQuery } from 'react-query';
import api from 'api';
import { useFormWithProvider } from 'forms';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { AdminCreateLoanFormData } from './types';
import type { User } from 'components/GlobalSearch/types';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { CACHE_TTL } from 'utils/cache';
import { formatTenure } from 'components/LoanPaymentV2/utils';
import {
  EMPLOYEE_SEARCH_LOAN_KEY,
  BASIC_USER_LOAN_DATA_KEY,
  LOAN_CATEGORIES_ADMIN_KEY,
  LOAN_CATEGORY_DETAIL_KEY,
} from './constants';
import { BasicUserLoanData } from 'components/LoanPaymentV2/types';
import { LoanCategory } from 'components/Settings/EmployeeLoansSettings/types';

export const BasicDetails = () => {
  const { setCurrentStep } = useWizardInfo();
  const { formData, setFormData } = useFormWithProvider<AdminCreateLoanFormData>();
  const [searchQuery, setSearchQuery] = useState('');
  const { debouncedValue: debouncedSearch } = useDebouncedValue(searchQuery, 300);
  const { data: searchData, isFetching: isSearching } = useQuery({
    queryKey: [EMPLOYEE_SEARCH_LOAN_KEY, debouncedSearch],
    queryFn: () => api.search.searchEmployees(debouncedSearch),
    enabled: !!debouncedSearch && debouncedSearch.length >= 2,
  });
  const searchResults: User[] = (searchData ?? []) as User[];

  const selectedEmployee = formData.employee;

  const { data: basicLoanData } = useQuery<BasicUserLoanData>({
    queryKey: [BASIC_USER_LOAN_DATA_KEY, selectedEmployee?.people_id],
    queryFn: () => api.loans.basicUserLoanData(selectedEmployee!.people_id),
    enabled: !!selectedEmployee,
  });

  const { data: categoriesData } = useQuery({
    queryKey: [LOAN_CATEGORIES_ADMIN_KEY],
    queryFn: () => api.loans.listLoanCategories(),
  });
  const categories = categoriesData?.data ?? [];

  const { data: categoryDetail } = useQuery<LoanCategory & { policy_document_url?: string }>({
    queryKey: [LOAN_CATEGORY_DETAIL_KEY, formData.loanCategoryId],
    queryFn: () => api.loans.getLoanCategory(formData.loanCategoryId!),
    enabled: !!formData.loanCategoryId,
    staleTime: CACHE_TTL.MEDIUM,
  });
  const categorySummary = categoryDetail?.policy_summary ?? null;

  const canProceed = !!formData.employee && !!formData.loanCategoryId;

  const selectedDisplayName = formData.employee
    ? `${formData.employee.name} (RZP${formData.employee.employee_id})`
    : '';
  const isEmployeeSelected = !!formData.employee && searchQuery === selectedDisplayName;
  const showResults = debouncedSearch.length >= 2 && !isEmployeeSelected;

  const handleEmployeeSelect = (user: User) => {
    const displayName = `${user.name} (RZP${user.employee_id})`;
    setFormData({ ...formData, employee: user, loanCategoryId: null, categoryName: '' });
    setSearchQuery(displayName);
  };

  return (
    <Box display="flex" flex={1} overflow="hidden">
      <FormWrapper
        onSubmit={(e) => {
          e.preventDefault();
          setCurrentStep(1);
        }}
        style={{ flex: 1, minWidth: 0 }}>
        <StepContentWrapper noBgIllustration width="462px">
          <Box display="flex" flexDirection="column" gap="spacing.8">
            <Box>
              <Heading size="medium" weight="semibold">
                Basic Details
              </Heading>
              <Text size="small" color="surface.text.gray.muted" marginTop="spacing.2">
                Select an employee and the reason for the request
              </Text>
            </Box>
            <Box display="flex" flexDirection="column" position="relative">
              <SearchInput
                label="Search For Employee"
                placeholder="Search by name or employee ID"
                value={searchQuery}
                onChange={({ value }) => {
                  const newValue = value ?? '';
                  setSearchQuery(newValue);
                  if (newValue !== selectedDisplayName) {
                    setFormData({ ...formData, employee: null });
                  }
                }}
                onClearButtonClick={() => {
                  setSearchQuery('');
                  setFormData({ ...formData, employee: null });
                }}
              />
              {searchQuery.length === 1 && (
                <Text size="small" color="surface.text.gray.muted" marginTop="spacing.2">
                  Type at least 2 characters to search
                </Text>
              )}
              {showResults && (
                <Box
                  position="absolute"
                  top="100%"
                  left="0px"
                  right="0px"
                  zIndex={10}
                  backgroundColor="surface.background.gray.intense"
                  borderWidth="thin"
                  borderRadius="medium"
                  borderColor="surface.border.gray.subtle"
                  marginTop="spacing.1"
                  elevation="lowRaised">
                  {isSearching ? (
                    <Box display="flex" justifyContent="center" padding="spacing.6">
                      <Spinner accessibilityLabel="Searching employees" />
                    </Box>
                  ) : searchResults.length > 0 ? (
                    <ActionList>
                      {searchResults.map((user) => (
                        <ActionListItem
                          key={user.people_id}
                          title={user.name}
                          description={`RZP${user.employee_id}`}
                          value={String(user.people_id)}
                          onClick={() => handleEmployeeSelect(user)}
                        />
                      ))}
                    </ActionList>
                  ) : (
                    <Box padding="spacing.5" textAlign="center">
                      <Text size="small" color="surface.text.gray.muted">
                        No employees found
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            {selectedEmployee && (
              <Box
                borderWidth="thin"
                borderRadius="medium"
                borderColor="surface.border.gray.subtle"
                padding="spacing.5"
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gap="spacing.4">
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Tenure
                  </Text>
                  <Text size="small" weight="semibold">
                    {formatTenure(basicLoanData?.hire_date)}
                  </Text>
                </Box>
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Annual Salary
                  </Text>
                  <Text size="small" weight="semibold">
                    {basicLoanData?.salary != null ? (
                      <Amount value={basicLoanData.salary} size="small" />
                    ) : (
                      '—'
                    )}
                  </Text>
                </Box>
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    Ongoing Loans
                  </Text>
                  <Text size="small" weight="semibold">
                    {basicLoanData
                      ? basicLoanData.existing_loan_count > 0
                        ? String(basicLoanData.existing_loan_count)
                        : 'None'
                      : '—'}
                  </Text>
                </Box>
                <Box>
                  <Text size="xsmall" color="surface.text.gray.muted">
                    In Notice Period
                  </Text>
                  <Text size="small" weight="semibold">
                    {basicLoanData ? (basicLoanData.deactivate_date ? 'Yes' : 'No') : '—'}
                  </Text>
                </Box>
              </Box>
            )}

            <Dropdown selectionType="single">
              <SelectInput
                label="Reason for Request"
                placeholder="Choose a reason"
                value={formData.loanCategoryId ? String(formData.loanCategoryId) : undefined}
                isDisabled={!selectedEmployee}
                onChange={({ values }) => {
                  const id = Number(values[0]);
                  const cat = categories.find((c) => c.id === id);
                  setFormData({
                    ...formData,
                    loanCategoryId: id,
                    categoryName: cat?.name ?? '',
                    isDocumentRequired: cat?.is_document_required ?? false,
                  });
                }}
              />
              <DropdownOverlay>
                <ActionList>
                  {categories.map((cat) => (
                    <ActionListItem key={cat.id} title={cat.name} value={String(cat.id)} />
                  ))}
                </ActionList>
              </DropdownOverlay>
            </Dropdown>
          </Box>
        </StepContentWrapper>

        <StepActionsWrapper width="462px">
          <Button variant="tertiary" onClick={() => setCurrentStep(-1)}>
            Cancel
          </Button>
          <Button type="submit" isDisabled={!canProceed}>
            Next
          </Button>
        </StepActionsWrapper>
      </FormWrapper>

      <PolicySummary
        policySummary={categorySummary}
        policyDocumentUrl={categoryDetail?.policy_document_url ?? null}
      />
    </Box>
  );
};
